import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";

import {
  DeleteInspectionFiles,
  ViewInspection,
  ViewInspectionFromAssignedProject,
  ManageDeletedData,
  DeleteInspectionFilesFromAssignedProject,
} from "../../services/authorization/Permissions";
import { SectionMenuItemsModel } from "../sewer-objects/section-details/SectionMenuItems";
import DataGrid from "../../components/datatable/DataGrid";
import PageLayout from "../_layout/PageLayout";
import { CurrentSectionStorageModel } from "../../services/sewer-object/CurrentSectionStorage";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import DeleteButton from "../../components/buttons/delete-button/DeleteButton";
import useErrorHandler, {
  ApplicationError,
} from "../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { InspectionFilesStorageModel } from "../../services/inspection/InspectionFilesStorage";
import { DownloadInspectionFilesModel } from "../../services/inspection/DownloadInspectionFilesModel";
import { translationService } from "../../services/translation/TranslationService";
import { api } from "../../services/api/ApiService";
import { DownloadInspectionFilesButton } from "./components/DataComponents";
import { CurrentInspectionStorageModel } from "../../services/inspection/CurrentInspectionStorage";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import useUrlParams from "../../services/url/UrlParamsService";
import { useHistory } from "react-router-dom";
import useRouteService from "../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";
import UploadFileToInspection from "./components/UploadFileToInspection";
import UpdateFileToInspection from "./components/UpdateFileToInspection";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { handleErrorInModal } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const [inspectionFilesStorage] = useState(() =>
    InspectionFilesStorageModel.create(
      {
        sectionId: currentSectionStorage.currentSectionId,
        inspectionId: currentInspectionId,
        useGridFiltering: true,
        uploadService: { isUploadInProgress: false },
      },
      { api }
    )
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentSectionStorage.currentSectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.parentEntityFromPath.id,
  ]);

  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  const downloadInspectionFilesState = useMemo(
    () =>
      DownloadInspectionFilesModel.create(
        {
          sectionName: currentSectionStorage.currentSection.name,
          inspectionName: getCurrentInspectionName(),
        },
        { translationService }
      ),
    [getCurrentInspectionName, currentSectionStorage.currentSection.name]
  );

  const getInspectionIsLockedWarning = useCallback(() => {
    if (
      currentInspectionStorage.inspectionIsLockedBySomeoneElse &&
      currentInspectionStorage.userWithLockOnInspection
    )
      return {
        key: "InspectionFilesPage.locked-inspection",
        context: { userName: currentInspectionStorage.userWithLockOnInspection.name },
      };
    else return undefined;
  }, [
    currentInspectionStorage.inspectionIsLockedBySomeoneElse,
    currentInspectionStorage.userWithLockOnInspection,
  ]);

  const isDeleteDisabled = useCallback(
    () =>
      !inspectionFilesStorage.anyItemsSelected ||
      !permissionsService.userHasOneOfThePermissions([
        DeleteInspectionFiles,
        DeleteInspectionFilesFromAssignedProject,
      ]) ||
      currentInspectionStorage.inspectionIsLockedBySomeoneElse,
    [
      currentInspectionStorage.inspectionIsLockedBySomeoneElse,
      inspectionFilesStorage.anyItemsSelected,
    ]
  );

  const confirmationModalSubtitleKey = useCallback(() => {
    return inspectionFilesStorage.isAnalysisOrSegmentationSuccessfullyDoneForSelectedFiles(
      inspectionFilesStorage.selectedItems
    )
      ? "damages.BatchProcessingDeleteVideo.warning.description"
      : "InspectionPage.delete.mediaFiles.confirmation";
  }, [inspectionFilesStorage]);

  const deleteSelectedFiles = useCallback(async () => {
    try {
      await inspectionFilesStorage.validateIfFilesCanBeDeleted(
        currentInspectionId,
        inspectionFilesStorage.selectedItems
      );
    } catch (error) {
      console.log(error);
      const applicationError = error as ApplicationError;
      applicationError.withModalTitleKey("damages.BatchProcessingDeleteVideo.error.title");
      handleErrorInModal(applicationError);
      return;
    }

    const selectedItemsCount = inspectionFilesStorage.selectedItemsCount;
    try {
      await inspectionFilesStorage.deleteInspectionFiles(
        currentInspectionId,
        inspectionFilesStorage.selectedItems
      );
      snackbarNotificationsService.showSuccess("InspectionPage.delete.mediaFile.success", {
        selectedFilesCount: selectedItemsCount,
      });
      await currentSectionStorage.load();
      await currentInspectionStorage.load();
    } catch (error) {
      const errorContext = { selectedFilesCount: selectedItemsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.mediaFiles.delete");
      handleErrorInSnackbar(applicationError);
    }
  }, [
    currentInspectionId,
    currentInspectionStorage,
    currentSectionStorage,
    handleErrorInModal,
    handleErrorInSnackbar,
    inspectionFilesStorage,
  ]);

  useEffect(() => {
    if (inspectionFilesStorage.uploadService.uploadFinished) {
      currentSectionStorage.load();
      currentInspectionStorage.load();
      inspectionFilesStorage.load();
    }
  }, [
    currentInspectionStorage,
    currentSectionStorage,
    inspectionFilesStorage,
    inspectionFilesStorage.uploadService.uploadFinished,
  ]);

  useEffect(() => {
    inspectionFilesStorage.setSectionAndInspection(
      currentSectionStorage.currentSectionId,
      currentInspectionId
    );
  }, [currentInspectionId, currentSectionStorage.currentSectionId, inspectionFilesStorage]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
        {
          entity: EntityTypeFromPath.Inspection,
          name: getCurrentInspectionName(),
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [
    currentSectionStorage,
    currentSectionStorage.currentSection,
    getCurrentInspectionName,
    history.location.pathname,
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewInspection, ViewInspectionFromAssignedProject]}
      titleKey="ViewInspectionFiles.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
      }}
      menuItems={menuItems}
      isLoading={
        currentInspectionStorage.isLoading ||
        inspectionFilesStorage.uploadService.isUploadInProgress
      }
    >
      <ActionsSection
        warningKey={getInspectionIsLockedWarning()?.key}
        warningContext={getInspectionIsLockedWarning()?.context}
      >
        <ButtonsSection>
          <DownloadInspectionFilesButton
            inspectionFilesStorage={inspectionFilesStorage}
            downloadInspectionFilesState={downloadInspectionFilesState}
          />
          <DeleteButton
            disabled={isDeleteDisabled()}
            confirmationAction={deleteSelectedFiles}
            confirmationModalTitleKey="InspectionPage.delete.mediaFiles.confirmation.title"
            confirmationModalTitleContext={{
              selectedFilesCount: inspectionFilesStorage.selectedItemsCount,
            }}
            confirmationModalSubtitleKey={confirmationModalSubtitleKey()}
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
          {featureFlagsStorage.isFeatureEnabled(
            Flags.Upload_Files_Individually_On_Inspection_Files_Page
          ) && (
            <UploadFileToInspection
              inspectionId={currentInspectionId}
              inspectionFilesStorage={inspectionFilesStorage}
              currentInspectionStorage={currentInspectionStorage}
            />
          )}
          <UpdateFileToInspection
            inspectionId={currentInspectionId}
            inspectionFilesStorage={inspectionFilesStorage}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid itemTypeKey="general.files" dataGridStorage={inspectionFilesStorage} />
    </PageLayout>
  );
});
